import { ProDescriptions } from '@ant-design/pro-components';
import { Card, Flex, List, Space, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';

const seedTypeValueEnum = {
  hotPot: { text: '热点采集' },
  seedPlan: { text: '创作规划' },
};

const factSections = [
  ['backgroundFacts', '背景事实'],
  ['dataPoints', '数据指标'],
  ['cases', '案例材料'],
  ['concepts', '概念定义'],
  ['userSignals', '用户信号'],
  ['controversies', '争议分歧'],
  ['timelines', '时间线'],
];

const factItemShape = PropTypes.shape({
  citationIds: PropTypes.arrayOf(PropTypes.string),
  confidence: PropTypes.string,
  evidence: PropTypes.string,
  limits: PropTypes.string,
  summary: PropTypes.string,
  title: PropTypes.string,
});

const citationShape = PropTypes.shape({
  credibility: PropTypes.string,
  position: PropTypes.number,
  publishedAt: PropTypes.string,
  sourceScore: PropTypes.string,
  summary: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string,
});

const getFactAnchor = (record) => record?.factAnchor || {};

const renderSeedTitle = (_, record) => record.seedTitle || record.seedId || '-';

const FactItemList = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <Typography.Text type="secondary">暂无材料</Typography.Text>;
  }
  return (
    <List
      size="small"
      dataSource={items}
      renderItem={(item) => (
        <List.Item>
          <Space vertical>
            <Space>
              <Typography.Text strong>{item.title || '-'}</Typography.Text>
              {item.confidence ? <Tag>{item.confidence}</Tag> : null}
            </Space>
            <Typography.Paragraph type="secondary">{item.summary || '-'}</Typography.Paragraph>
            <Typography.Paragraph>{item.evidence || '-'}</Typography.Paragraph>
            <Typography.Text type="secondary">适用边界：{item.limits || '-'}</Typography.Text>
          </Space>
        </List.Item>
      )}
    />
  );
};

const CitationList = ({ citations }) => {
  if (!Array.isArray(citations) || citations.length === 0) {
    return <Typography.Text type="secondary">暂无出处</Typography.Text>;
  }
  return (
    <List
      size="small"
      dataSource={citations}
      renderItem={(item) => (
        <List.Item>
          <Space vertical>
            {item.url ? (
              <a href={item.url} target="_blank" rel="noreferrer">
                {item.title || item.url}
              </a>
            ) : (
              <Typography.Text>{item.title || '-'}</Typography.Text>
            )}
            <Typography.Text type="secondary">
              {[
                item.publishedAt,
                item.sourceScore ? `评分：${item.sourceScore}` : null,
                item.position ? `排序：${item.position}` : null,
                item.credibility,
              ]
                .filter(Boolean)
                .join(' / ') || '-'}
            </Typography.Text>
            {item.summary ? (
              <Typography.Paragraph type="secondary">{item.summary}</Typography.Paragraph>
            ) : null}
          </Space>
        </List.Item>
      )}
    />
  );
};

const FactAnchorPreview = ({ record }) => {
  const factAnchor = getFactAnchor(record);
  return (
    <Flex vertical gap="middle">
      <ProDescriptions
        column={1}
        dataSource={record || {}}
        columns={[
          { title: '种子标题', dataIndex: 'seedTitle', render: renderSeedTitle },
          { title: '种子类型', dataIndex: 'seedType', valueEnum: seedTypeValueEnum },
          { title: '搜集时间', dataIndex: 'createdAt', valueType: 'dateTime' },
        ]}
      />
      {factSections.map(([key, title]) => (
        <Card key={key} size="small" title={title} type="inner">
          <FactItemList items={factAnchor[key]} />
        </Card>
      ))}
      <Card size="small" title="出处" type="inner">
        <CitationList citations={factAnchor.citations} />
      </Card>
    </Flex>
  );
};

FactItemList.propTypes = {
  items: PropTypes.arrayOf(factItemShape),
};

CitationList.propTypes = {
  citations: PropTypes.arrayOf(citationShape),
};

FactAnchorPreview.propTypes = {
  record: PropTypes.shape({
    factAnchor: PropTypes.shape({
      backgroundFacts: PropTypes.arrayOf(factItemShape),
      cases: PropTypes.arrayOf(factItemShape),
      citations: PropTypes.arrayOf(citationShape),
      concepts: PropTypes.arrayOf(factItemShape),
      controversies: PropTypes.arrayOf(factItemShape),
      dataPoints: PropTypes.arrayOf(factItemShape),
      timelines: PropTypes.arrayOf(factItemShape),
      userSignals: PropTypes.arrayOf(factItemShape),
    }),
  }),
};

FactAnchorPreview.defaultProps = {
  record: undefined,
};

export default FactAnchorPreview;
